import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import Slider from "react-slick";
import { getSliders, getUploadUrl } from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage, pickLang } from "../../contexts/LanguageContext";
import "./HeroSlider.css";
import { getSliderImage } from "../../utils/sliderImage";

const HeroSlider = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageRatios, setImageRatios] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const { language } = useLanguage();

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await getSliders();
      if (response.data.success) {
        setSlides(response.data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sliders:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!slides.length) return;

    let isCancelled = false;

    const detectImageRatios = async () => {
      const checks = slides.map((slide) => {
        const foto = getSliderImage(slide, isMobile);
        if (!foto) {
          return Promise.resolve({ id: slide.id, ratio: 16 / 9 });
        }

        const imageUrl = getUploadUrl(foto);
        return new Promise((resolve) => {
          const img = new Image();

          img.onload = () => {
            // Preserve the original aspect ratio on mobile, including wide banners.
            const ratio = img.naturalWidth / img.naturalHeight;

            resolve({ id: slide.id, ratio: ratio || 16 / 9 });
          };

          img.onerror = () => {
            resolve({ id: slide.id, ratio: 16 / 9 });
          };

          img.src = imageUrl;
        });
      });

      const results = await Promise.all(checks);
      if (isCancelled) return;

      const nextRatios = {};
      results.forEach(({ id, ratio }) => {
        nextRatios[id] = ratio;
      });
      setImageRatios(nextRatios);
    };

    detectImageRatios();

    return () => {
      isCancelled = true;
    };
  }, [slides, isMobile]);

  // Custom arrow components
  const NextArrow = ({ onClick }) => {
    return (
      <button
        className="slick-arrow slick-next"
        onClick={onClick}
        aria-label="Next slide"
      >
        <i className="fas fa-chevron-right"></i>
      </button>
    );
  };

  const PrevArrow = ({ onClick }) => {
    return (
      <button
        className="slick-arrow slick-prev"
        onClick={onClick}
        aria-label="Previous slide"
      >
        <i className="fas fa-chevron-left"></i>
      </button>
    );
  };

  const settings = {
    dots: true,
    infinite: slides.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: slides.length > 1,
    autoplaySpeed: 5000,
    fade: true,
    cssEase: "linear",
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    pauseOnHover: true,
    adaptiveHeight: false,
    responsive: [{ breakpoint: 769, settings: { adaptiveHeight: true } }],
    dotsClass: "slick-dots",
  };

  const { orgName } = useTheme();

  const slideHasText = (slide) => {
    const judul = (pickLang(slide, "judul", language) || "").trim();
    const singkat = (
      pickLang(slide, "deskripsisingkat", language) || ""
    ).trim();
    const deskripsi = (pickLang(slide, "deskripsi", language) || "").trim();
    return !!(judul || singkat || deskripsi);
  };

  if (loading) {
    return (
      <div className="slider-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="hero-slider default-slide">
        <div className="slide-content">
          <div className="container">
            <h1>
              {orgName} -{" "}
              {language === "en"
                ? "Halal Education Indonesia"
                : "Edukasi Halal Indonesia"}
            </h1>
            <p>
              {language === "en"
                ? "Indonesian Halal Product Supervisory and Oversight Institution"
                : "Lembaga Penyelia dan Pengawas Produk Halal Indonesia"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-slider">
      <Slider {...settings}>
        {slides.map((slide) => (
          <div key={slide.id}>
            <div
              className={`slide-item ${getSliderImage(slide, isMobile) ? "has-banner-image" : ""} ${
                !slideHasText(slide) && !(slide.teks_tombol && slide.link) ? "image-only" : ""
              }`}
              style={{
                "--slide-aspect-ratio": imageRatios[slide.id] || 16 / 9,
                backgroundImage: getSliderImage(slide, isMobile)
                  ? `url(${getUploadUrl(getSliderImage(slide, isMobile))})`
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              {slideHasText(slide) && <div className="slide-overlay"></div>}
              <div className="slide-content">
                <div className="container">
                  <h1>{pickLang(slide, "judul", language)}</h1>
                  {pickLang(slide, "deskripsisingkat", language) && (
                    <p className="lead">
                      {pickLang(slide, "deskripsisingkat", language)}
                    </p>
                  )}
                  {pickLang(slide, "deskripsi", language) && (
                    <div
                      className="slide-description"
                      dangerouslySetInnerHTML={{
                        __html: pickLang(slide, "deskripsi", language),
                      }}
                    />
                  )}
                  {slide.teks_tombol && slide.link && (
                    <div className="slide-button-container">
                      {slide.link.startsWith("http") ||
                      slide.link.startsWith("https") ? (
                        <Button
                          as="a"
                          href={slide.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="light"
                          size="lg"
                          className="slide-button"
                        >
                          {pickLang(slide, "teks_tombol", language)}
                        </Button>
                      ) : (
                        <Button
                          as={Link}
                          to={slide.link}
                          variant="light"
                          size="lg"
                          className="slide-button"
                        >
                          {pickLang(slide, "teks_tombol", language)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default HeroSlider;
